/**
 * Aggregate Tactical Domain Model
 * 
 * Rich domain model for DDD Aggregates with business invariants, 
 * domain events, and entity relationships. Bridges strategic and 
 * tactical modeling by connecting to bounded contexts.
 */

export interface AggregateEntity {
  id: string;
  name: string;
  description?: string;
  properties: AggregateProperty[];
  methods: AggregateMethod[];
}

export interface ValueObjectDefinition {
  id: string;
  name: string;
  description?: string;
  properties: ValueProperty[];
  validationRules: ValidationRule[];
}

export interface AggregateProperty {
  id: string;
  name: string;
  type: PropertyType;
  description?: string;
  isRequired: boolean;
  isCollection: boolean;
  constraints?: PropertyConstraint[];
}

export interface AggregateMethod {
  id: string;
  name: string;
  description?: string;
  parameters: MethodParameter[];
  returnType?: string;
  businessRule?: string;
  emittedEvents: string[]; // Domain event names
}

export interface MethodParameter {
  name: string;
  type: PropertyType;
  isRequired: boolean;
  description?: string;
}

export interface ValueProperty {
  id: string;
  name: string;
  type: PropertyType;
  description?: string;
  isRequired: boolean;
  constraints?: PropertyConstraint[];
}

export interface ValidationRule {
  id: string;
  description: string;
  expression: string; // Business rule expression
  errorMessage: string;
}

export interface PropertyConstraint {
  type: 'min_length' | 'max_length' | 'min_value' | 'max_value' | 'pattern' | 'custom';
  value: string | number;
  description?: string;
}

export interface BusinessInvariant {
  businessInvariantId: string;
  description: string;
  rule: string; // Business rule expression
  severity: 'error' | 'warning';
  scope: 'aggregate' | 'entity' | 'cross_entity';
}

export interface DomainEvent {
  domainEventId: string;
  name: string;
  description?: string;
  properties: EventProperty[];
  triggers: string[]; // What business actions trigger this event
}

export interface EventProperty {
  name: string;
  type: PropertyType;
  description?: string;
  isRequired: boolean;
}

export type PropertyType = 
  | 'string' 
  | 'number' 
  | 'boolean' 
  | 'date' 
  | 'uuid' 
  | 'email' 
  | 'url'
  | { valueObject: string } // Reference to value object
  | { entity: string }      // Reference to entity
  | { enum: string[] };     // Enumeration values

export interface AggregateProperties {
  boundedContextId: string;
  name: string;
  description?: string;
  responsibility: string; // Core business responsibility
  rootEntity: AggregateEntity;
  childEntities: AggregateEntity[];
  valueObjects: ValueObjectDefinition[];
  businessInvariantIds: string[];
  domainEventsIds: string[];
  ubiquitousLanguageTerms: string[]; // References to UL terms
}

export class Aggregate {
  readonly id: string;
  readonly createdAt: Date;
  private _updatedAt: Date;
  private _boundedContextId: string;
  private _name: string;
  private _description?: string;
  private _responsibility: string;
  private _rootEntity: AggregateEntity;
  private _childEntities: AggregateEntity[];
  private _valueObjects: ValueObjectDefinition[];
  private _businessInvariantIds: string[];
  private _domainEventsIds: string[];
  private _ubiquitousLanguageTerms: string[];

  private constructor(properties: AggregateProperties, id?: string) {
    this.id = id || crypto.randomUUID();
    this.createdAt = new Date();
    this._updatedAt = new Date();
    this._boundedContextId = properties.boundedContextId;
    this._name = properties.name;
    this._description = properties.description;
    this._responsibility = properties.responsibility;
    this._rootEntity = properties.rootEntity;
    this._childEntities = properties.childEntities;
    this._valueObjects = properties.valueObjects;
    this._businessInvariantIds = properties.businessInvariantIds;
    this._domainEventsIds = properties.domainEventsIds;
    this._ubiquitousLanguageTerms = properties.ubiquitousLanguageTerms;

    this.validateInvariants();
  }

  static create(properties: AggregateProperties): Aggregate {
    return new Aggregate(properties);
  }

  static reconstitute(
    properties: AggregateProperties,
    id: string,
    createdAt: Date,
    updatedAt: Date
  ): Aggregate {
    const aggregate = new Aggregate(properties, id);
    (aggregate as any).createdAt = createdAt;
    aggregate._updatedAt = updatedAt;
    return aggregate;
  }

  // Getters
  get boundedContextId(): string { return this._boundedContextId; }
  get name(): string { return this._name; }
  get description(): string | undefined { return this._description; }
  get responsibility(): string { return this._responsibility; }
  get rootEntity(): AggregateEntity { return { ...this._rootEntity }; }
  get childEntities(): AggregateEntity[] { return [...this._childEntities]; }
  get valueObjects(): ValueObjectDefinition[] { return [...this._valueObjects]; }
  get businessInvariantIds(): string[] { return [...this._businessInvariantIds]; }
  get domainEventIds(): string[] { return [...this._domainEventsIds]; }
  get ubiquitousLanguageTerms(): string[] { return [...this._ubiquitousLanguageTerms]; }
  get updatedAt(): Date { return this._updatedAt; }

  // Business methods
  updateName(name: string): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Aggregate name cannot be empty');
    }
    this._name = name.trim();
    this.touch();
  }

  updateDescription(description?: string): void {
    this._description = description?.trim() || undefined;
    this.touch();
  }

  updateResponsibility(responsibility: string): void {
    if (!responsibility || responsibility.trim().length === 0) {
      throw new Error('Aggregate responsibility cannot be empty');
    }
    this._responsibility = responsibility.trim();
    this.touch();
  }

  addChildEntity(entity: AggregateEntity): void {
    if (!entity.name) {
      throw new Error('Entity name is required');
    }
    if (this._childEntities.find(e => e.id === entity.id)) {
      throw new Error('Entity already exists in aggregate');
    }
    this._childEntities.push(entity);
    this.touch();
  }

  removeChildEntity(entityId: string): void {
    const index = this._childEntities.findIndex(e => e.id === entityId);
    if (index > -1) {
      this._childEntities.splice(index, 1);
      this.touch();
    }
  }

  addValueObject(valueObject: ValueObjectDefinition): void {
    if (!valueObject.name) {
      throw new Error('Value object name is required');
    }
    if (this._valueObjects.find(vo => vo.id === valueObject.id)) {
      throw new Error('Value object already exists in aggregate');
    }
    this._valueObjects.push(valueObject);
    this.touch();
  }

  removeValueObject(valueObjectId: string): void {
    const index = this._valueObjects.findIndex(vo => vo.id === valueObjectId);
    if (index > -1) {
      this._valueObjects.splice(index, 1);
      this.touch();
    }
  }

  addBusinessInvariant(businessInvariantId: string): void {
    if (!businessInvariantId || businessInvariantId.length == 0) {
      throw new Error(`Business invariant id must be a valid string`);
    }
    if (this._businessInvariantIds.find(bi => bi === businessInvariantId)) {
      throw new Error('Business invariant already exists');
    }
    this._businessInvariantIds.push(businessInvariantId);
    this.touch();
  }

  removeBusinessInvariant(businessInvariantId: string): void {
    const index = this._businessInvariantIds.findIndex(bi => bi === businessInvariantId);
    if (index > -1) {
      this._businessInvariantIds.splice(index, 1);
      this.touch();
    }
  }

  addDomainEvent(domainEventId: string): void {
    if (!domainEventId || domainEventId.length === 0) {
      throw new Error('Domain event ID is required');
    }
    if (this._domainEventsIds.find(de => de === domainEventId)) {
      throw new Error('Domain event already exists');
    }
    this._domainEventsIds.push(domainEventId);
    this.touch();
  }

  removeDomainEvent(domainEventId: string): void {
    const index = this._domainEventsIds.findIndex(de => de === domainEventId);
    if (index > -1) {
      this._domainEventsIds.splice(index, 1);
      this.touch();
    }
  }

  addUbiquitousLanguageTerm(term: string): void {
    if (!term || term.trim().length === 0) {
      throw new Error('Ubiquitous language term cannot be empty');
    }
    const trimmedTerm = term.trim();
    if (!this._ubiquitousLanguageTerms.includes(trimmedTerm)) {
      this._ubiquitousLanguageTerms.push(trimmedTerm);
      this.touch();
    }
  }

  removeUbiquitousLanguageTerm(term: string): void {
    const index = this._ubiquitousLanguageTerms.indexOf(term);
    if (index > -1) {
      this._ubiquitousLanguageTerms.splice(index, 1);
      this.touch();
    }
  }

  // Analysis methods
  getComplexityScore(): number {
    // Simple complexity scoring based on aggregate characteristics
    let score = 0;
    score += this._childEntities.length * 2; // Child entities add complexity
    score += this._valueObjects.length; // Value objects add some complexity
    score += this._businessInvariantIds.length * 3; // Business rules add significant complexity
    score += this._domainEventsIds.length; // Events indicate business activity
    
    // Normalize to 0-100 scale
    return Math.min(100, score);
  }

  getCohesionScore(): number {
    // Simple cohesion scoring based on shared UL terms and focused responsibility
    const ulTermCount = this._ubiquitousLanguageTerms.length;
    const totalElements = this._childEntities.length + this._valueObjects.length + 1; // +1 for root
    
    // Higher UL term density indicates better cohesion
    const termDensity = ulTermCount / totalElements;
    
    // Responsibility clarity (longer descriptions often indicate fuzzy responsibilities)
    const responsibilityClaritylength = this._responsibility.length;
    const clarityScore = responsibilityClaritylength > 200 ? 50 : 100 - (responsibilityClaritylength / 4);
    
    return Math.min(100, (termDensity * 50) + (clarityScore * 0.5));
  }

  private touch(): void {
    this._updatedAt = new Date();
  }

  private validateInvariants(): void {
    // Business Invariant: Aggregate ID must follow AR-XXX pattern (Eric Evans DDD)
    if (!this.id.match(/^AR-\d{3}$/)) {
      throw new Error('Aggregate ID must follow AR-XXX pattern (e.g., AR-001, AR-015) to maintain domain model integrity');
    }

    if (!this._boundedContextId) {
      throw new Error('Bounded context ID is required');
    }

    if (!this._name || this._name.trim().length === 0) {
      throw new Error('Aggregate name is required');
    }

    if (this._name.length > 100) {
      throw new Error('Aggregate name must be 100 characters or less');
    }

    if (!this._responsibility || this._responsibility.trim().length === 0) {
      throw new Error('Aggregate responsibility is required');
    }

    if (this._responsibility.length > 500) {
      throw new Error('Aggregate responsibility must be 500 characters or less');
    }

    if (!this._rootEntity || !this._rootEntity.name) {
      throw new Error('Aggregate root entity is required');
    }

    if (this._description && this._description.length > 1000) {
      throw new Error('Aggregate description must be 1000 characters or less');
    }
  }

  toJSON() {
    return {
      id: this.id,
      boundedContextId: this._boundedContextId,
      name: this._name,
      description: this._description,
      responsibility: this._responsibility,
      rootEntity: this._rootEntity,
      childEntities: this._childEntities,
      valueObjects: this._valueObjects,
      businessInvariantIds: this._businessInvariantIds,
      domainEventsIds: this._domainEventsIds,
      ubiquitousLanguageTerms: this._ubiquitousLanguageTerms,
      complexityScore: this.getComplexityScore(),
      cohesionScore: this.getCohesionScore(),
      createdAt: this.createdAt,
      updatedAt: this._updatedAt
    };
  }
}